import { useEffect, useRef } from 'react';

import type { PendingOrder, PendingOrderInventory } from 'apis/PendingOrdersApi';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import GMetrixToast from 'components/GMetrixToast/GMetrixToast';
import { usePendingOrders } from 'pages/pending_orders/context';
import { PendingOrdersApi } from 'apis/PendingOrdersApi';
import { helpContent } from 'pages/pending_orders/help';
import { FormattedMessage, useIntl } from 'react-intl';
import { PageHeader } from 'components/layout/page';
import { Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

export const PendingOrdersList = () => {

    const [state, setState] = useSuperState({
        actionLoading: false,
        inventory: [] as PendingOrderInventory[],
        loading: false,
        orders: [] as PendingOrder[],
        purchaseOrders: {} as Record<number, string>,
        selectedOrderIds: [] as number[]
    });
    const { actionLoading, inventory, loading, orders, purchaseOrders, selectedOrderIds } = state;

    const { highestRole, selectedPartner } = useApp();
    const { permissions } = usePendingOrders();
    const { formatMessage } = useIntl();
    const effectivePartnerId = selectedPartner?.id ?? highestRole.partnerId;

    const toastRef = useRef<any>(null);

    const getInventoryForLicense = (licenseId: number): number => {

        const match = inventory.find(i => i.LicenseID === licenseId);
        return match?.Inventory ?? 0;

    };

    const loadData = async () => {

        try {

            setState({ loading: true });

            const [ordersResult, inventoryResult] = await Promise.all([
                PendingOrdersApi.GetPendingOrders(effectivePartnerId),
                PendingOrdersApi.GetInventory(effectivePartnerId)
            ]);

            const poMap: Record<number, string> = {};
            (ordersResult ?? []).forEach(o => {

                poMap[o.ID] = o.PurchaseOrder ?? '';

            });

            setState({
                inventory: inventoryResult ?? [],
                loading: false,
                orders: ordersResult ?? [],
                purchaseOrders: poMap,
                selectedOrderIds: []
            });

        } catch (e: any) {

            setState({ loading: false });
            toastRef.current?.createToast(e?.message ?? 'Failed to load pending orders.', 'danger');

        }

    };

    const toggleOrderSelection = (orderId: number) => {

        const isSelected = selectedOrderIds.includes(orderId);
        setState({
            selectedOrderIds: isSelected
                ? selectedOrderIds.filter(id => id !== orderId)
                : [...selectedOrderIds, orderId]
        });

    };

    const updatePurchaseOrder = (orderId: number, value: string) => {

        setState({
            purchaseOrders: { ...purchaseOrders, [orderId]: value }
        });

    };

    const getSelectedOrders = (): PendingOrder[] => orders.filter(o => selectedOrderIds.includes(o.ID));

    const handleCancelSelected = async () => {

        const selected = getSelectedOrders();
        if (selected.length === 0) return;

        try {

            setState({ actionLoading: true });
            const results = await PendingOrdersApi.CancelOrders(
                selected.map(o => o.ID),
                selected.map(o => o.Comment ?? '')
            );

            const allSuccessful = results.every(r => r.Done);
            const allFailed = results.every(r => !r.Done);

            if (allSuccessful) {

                toastRef.current?.createToast(formatMessage({ id: 'all_orders_cancelled_successfully.cc28621c', defaultMessage: 'All orders cancelled successfully.' }), 'success');

            } else if (allFailed) {

                toastRef.current?.createToast(results.map(r => r.Message).join(' | '), 'danger');

            } else {

                toastRef.current?.createToast(results.map(r => r.Message).join(' | '), 'warning');

            }

            setState({ actionLoading: false });
            await loadData();

        } catch (e: any) {

            setState({ actionLoading: false });
            toastRef.current?.createToast(e?.message ?? 'Failed to cancel orders.', 'danger');

        }

    };

    const handleFillWithInventory = async () => {

        const selected = getSelectedOrders();
        if (selected.length === 0) return;

        if (highestRole.id !== 1) {

            const licenseGroups: Record<number, number> = {};
            selected.forEach(o => {

                licenseGroups[o.LicenseId] = (licenseGroups[o.LicenseId] ?? 0) + o.NumRequested;

            });

            const hasAdequateInventory = Object.entries(licenseGroups).every(([licenseId, totalRequested]) => {

                const available = getInventoryForLicense(Number(licenseId));
                return totalRequested <= available;

            });

            if (!hasAdequateInventory) {

                toastRef.current?.createToast(formatMessage({ id: 'you_do_not_have_adequate_inventory_to_fi.a5236a72', defaultMessage: 'You do not have adequate inventory to fill the selected order(s).' }), 'danger');
                return;

            }

        }

        try {

            setState({ actionLoading: true });

            const results = await PendingOrdersApi.FillWithInventory(
                effectivePartnerId,
                selected.map(o => o.ID),
                selected.map(o => o.LicenseId),
                selected.map(o => o.NumRequested),
                selected.map(o => purchaseOrders[o.ID] ?? ''),
                selected.map(o => o.Comment ?? '')
            );

            const allSuccessful = results.every(r => r.Done);
            const allFailed = results.every(r => !r.Done);

            if (allSuccessful) {

                toastRef.current?.createToast(formatMessage({ id: 'all_orders_placed_successfully.5414336f', defaultMessage: 'All orders placed successfully.' }), 'success');

            } else if (allFailed) {

                toastRef.current?.createToast(results.map(r => r.Message).join(' | '), 'danger');

            } else {

                toastRef.current?.createToast(results.map(r => r.Message).join(' | '), 'warning');

            }

            setState({ actionLoading: false });
            await loadData();

        } catch (e: any) {

            setState({ actionLoading: false });
            toastRef.current?.createToast(e?.message ?? 'Failed to fill orders.', 'danger');

        }

    };

    const handleAutofill = async () => {

        const selected = getSelectedOrders();
        if (selected.length === 0) return;

        try {

            setState({ actionLoading: true });

            const results = await PendingOrdersApi.AutofillOrders(
                effectivePartnerId,
                selected.map(o => o.ID),
                selected.map(o => o.LicenseId),
                selected.map(o => o.NumRequested),
                selected.map(o => purchaseOrders[o.ID] ?? ''),
                selected.map(o => o.Comment ?? '')
            );

            const allSuccessful = results.every(r => r.Done);
            const allFailed = results.every(r => !r.Done);

            if (allSuccessful) {

                toastRef.current?.createToast(formatMessage({ id: 'all_orders_placed_successfully.5414336f', defaultMessage: 'All orders placed successfully.' }), 'success');

            } else if (allFailed) {

                toastRef.current?.createToast(results.map(r => r.Message).join(' | '), 'danger');

            } else {

                toastRef.current?.createToast(results.map(r => r.Message).join(' | '), 'warning');

            }

            setState({ actionLoading: false });
            await loadData();

        } catch (e: any) {

            setState({ actionLoading: false });
            toastRef.current?.createToast(e?.message ?? 'Failed to autofill orders.', 'danger');

        }

    };

    useEffect(() => {

        loadData();

    }, []);

    return (
        <div className="pending-orders-list d-flex flex-column gap-3">
            <GMetrixToast ref={toastRef} />

            <PageHeader
                actions={[
                    {
                        disabled: selectedOrderIds.length === 0 || actionLoading,
                        label: formatMessage({ id: 'cancel_selected_orders.583ba2c1', defaultMessage: 'Cancel Selected Orders' }),
                        onClick: handleCancelSelected
                    },
                    {
                        disabled: selectedOrderIds.length === 0 || actionLoading,
                        label: formatMessage({ id: 'fill_with_existing_inventory.5de012e1', defaultMessage: 'Fill with Existing Inventory' }),
                        onClick: handleFillWithInventory
                    },
                    ...(permissions.CanFillNewOrder ? [{
                        disabled: selectedOrderIds.length === 0 || actionLoading,
                        label: formatMessage({ id: 'fill_with_new_order.1c67b38b', defaultMessage: 'Fill with New Order' }),
                        onClick: handleAutofill
                    }] : [])
                ]}
                helpContent={helpContent}
                title={formatMessage({ id: 'pending_orders.abd66f4e', defaultMessage: 'Pending Orders' })}
            />

            <Card body>
                <div className="d-flex justify-content-between mb-4">
                    <div>
                        {<FormattedMessage defaultMessage="Note: The Fill with New Order button will place an order for you to your supplier for that license. When your supplier fills that order, the order to your subordinate will automatically be filled." id="note_the_fill_with_new_order_button_will.631ee283" />}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <GMetrixLoader size="large" />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center p-4 text-muted">
                        <FormattedMessage defaultMessage="No pending orders." id="no_pending_orders.af94b811" />
                    </div>
                ) : (
                    <div className="d-flex flex-wrap gap-3 justify-content-start">
                        {orders.map(order => (
                            <div
                                className="card border-primary shadow-sm p-3 mb-4 col-12 col-lg-5"
                                key={order.ID}
                                style={{ maxWidth: '600px' }}
                            >
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div className="form-check">
                                        <input
                                            checked={selectedOrderIds.includes(order.ID)}
                                            className="form-check-input pe-auto"
                                            id={`chk-${order.ID}`}
                                            onChange={() => toggleOrderSelection(order.ID)}
                                            type="checkbox"
                                        />
                                        <label className="form-check-label fw-bold ms-2" htmlFor={`chk-${order.ID}`}>
                                            {order.PartnerName}
                                        </label>
                                    </div>
                                    <small className="text-muted">
                                        {new Date(order.DateCreate).toLocaleString()}
                                    </small>
                                </div>

                                <h5 className="card-title">
                                    <FormattedMessage defaultMessage="Purchase Order" id="purchase_order.17844493" />
                                </h5>

                                <div className="mb-3">
                                    <input
                                        className="form-control"
                                        onChange={e => updatePurchaseOrder(order.ID, e.target.value)}
                                        placeholder={formatMessage({ id: 'enter_purchase_order.191321ad', defaultMessage: 'Enter purchase order' })}
                                        type="text"
                                        value={purchaseOrders[order.ID] ?? ''}
                                    />
                                </div>

                                <div className="row">
                                    <div className="col-12 col-sm-6 mb-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Order Placed by:" id="order_placed_by.3bbda786" />
                                        </strong>
                                        {' '}
                                        <span>
                                            {order.RequestedUserFirstName}
                                            {' '}
                                            {order.RequestedUserLastName}
                                        </span>
                                    </div>
                                    <div className="col-12 col-sm-6 mb-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="License:" id="license.25d04c71" />
                                        </strong>
                                        {' '}
                                        <span>
                                            {order.LicenseName}
                                        </span>
                                    </div>
                                    <div className="col-12 col-sm-6 mb-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Requested:" id="requested.6bcf56bc" />
                                        </strong>
                                        {' '}
                                        <span>
                                            {order.NumRequested}
                                        </span>
                                    </div>
                                    <div className="col-12 col-sm-6 mb-2">
                                        <strong>
                                            <FormattedMessage defaultMessage="Inventory:" id="inventory.eae6012e" />
                                        </strong>
                                        {' '}
                                        <span>
                                            {getInventoryForLicense(order.LicenseId)}
                                        </span>
                                    </div>
                                </div>

                                {(order.OrderChain?.length > 0 || order.RenewedByOrderControl) && (
                                    <div className="row">
                                        <div>
                                            {order.OrderChain?.length > 0 && (
                                                <>
                                                    <br />
                                                    <b>
                                                        <FormattedMessage defaultMessage="Order Chain:" id="order_chain.81bc2da9" />
                                                    </b>
                                                    {order.OrderChain.map(chain => (
                                                        <span key={chain.ID}>
                                                            {' -> '}
                                                            <span className={`OrderChain ${chain.PartnerType}`}>
                                                                {chain.PartnerName}
                                                            </span>
                                                            {chain.RenewedByOrderControl && (
                                                                <>
                                                                    <br />
                                                                    <span>
                                                                        &#x21bb;
                                                                        {' '}
                                                                        <FormattedMessage defaultMessage="This Order Renews:" id="this_order_renews.f858aab2" />
                                                                        {' '}
                                                                        <b>
                                                                            {chain.RenewedByOrderControl}
                                                                        </b>
                                                                    </span>
                                                                </>
                                                            )}
                                                        </span>
                                                    ))}
                                                    <br />
                                                </>
                                            )}
                                            {order.RenewedByOrderControl && (
                                                <>
                                                    <br />
                                                    <span>
                                                        &#x21bb;
                                                        {' '}
                                                        <FormattedMessage defaultMessage="This Order Renews:" id="this_order_renews.f858aab2" />
                                                        {' '}
                                                        <b>
                                                            {order.RenewedByOrderControl}
                                                        </b>
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );

};
